import { useEffect, useState } from "react";
import { Button, Collapse, Select, Spin } from "antd";
import { useDropdowns } from "@utils/customHooks";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import "./filter.scss";
import { useChronicBreakdownStore } from "@stores/chronicBreakdownStore";
import { isEmpty, map } from "lodash";
import { MultiSelectOpen } from "../../qualityMeasureReport/filters/multiselect";
import memberDetailsService from "@services/memberDetails.service";
import { usePopulationInsightStore } from "@stores/populationInsightStore";
import reportService from "@services/report.service";
import { getEffectiveYear } from "@utils/helpers";

const Filters = () => {
  const { loadingOptions, getOptions, filterDropdowns } = useDropdowns();
  const loadingState = useChronicBreakdownStore((state) => state.loadingState);
  const setLoadingState = useChronicBreakdownStore((state) => state.setLoadingState);
  const loadingCity = useChronicBreakdownStore((state) => state.loadingCity);
  const setLoadingCity = useChronicBreakdownStore((state) => state.setLoadingCity);
  const loadingHealthPlan = useChronicBreakdownStore((state) => state.loadingHealthPlan);
  const setLoadingHealthPlan = useChronicBreakdownStore((state) => state.setLoadingHealthPlan);
  const loadingPcpVendor = useChronicBreakdownStore((state) => state.loadingPcpVendor);
  const setLoadingPcpVendor = useChronicBreakdownStore((state) => state.setLoadingPcpVendor);
  const loadingProviders = useChronicBreakdownStore((state) => state.loadingProviders);
  const setLoadingProviders = useChronicBreakdownStore((state) => state.setLoadingProviders);
  const [flag, setFlag] = useState(false);
  const [hasInitialFilterRun, setHasInitialFilterRun] = useState(false);
  const prevAppliedFilters = useChronicBreakdownStore((state) => state.appliedProvider);
  const setPrevAppliedFilters = useChronicBreakdownStore((state) => state.setAppliedProvider);
  const selectedCity = useChronicBreakdownStore((state) => state.selectedCity);
  const setSelectedCity = useChronicBreakdownStore((state) => state.setSelectedCity);
  const selectedState = useChronicBreakdownStore((state) => state.selectedState);
  const setSelectedState = useChronicBreakdownStore((state) => state.setSelectedState);
  const selectedProvider = useChronicBreakdownStore((state) => state.selectedProvider);
  const setSelectedProvider = useChronicBreakdownStore((state) => state.setSelectedProvider);
  const selectedPCPVendor = useChronicBreakdownStore((state) => state.selectedPCPVendor);
  const setSelectedPcpVendor = useChronicBreakdownStore((state) => state.setSelectedPcpVendor);
  const selectedHealthPlan = useChronicBreakdownStore((state) => state.selectedHealthPlan);
  const setSelectedHealthPlan = useChronicBreakdownStore((state) => state.setSelectedHealthPlan);
  const stateOptions = useChronicBreakdownStore((state) => state.stateOptions);
  const setStateOptions = useChronicBreakdownStore((state) => state.setStateOptions);
  const cityOptions = useChronicBreakdownStore((state) => state.cityOptions);
  const setCityOptions = useChronicBreakdownStore((state) => state.setCityOptions);
  const providerOptions = useChronicBreakdownStore((state) => state.options);
  const setProviderOptions = useChronicBreakdownStore((state) => state.setOptions);
  const healthPlanOptions = useChronicBreakdownStore((state) => state.healthPlanOptions);
  const setHealthPlanOptions = useChronicBreakdownStore((state) => state.setHealthPlanOptions);
  const pcpVendorOptions = useChronicBreakdownStore((state) => state.pcpVendorOptions);
  const setPcpVendorOptions = useChronicBreakdownStore((state) => state.setPcpVendorOptions);
  const comingFromMembers = usePopulationInsightStore((state) => state.comingFromMembers);
  const handleComingFromMembersFlag = usePopulationInsightStore((state) => state.handleComingFromMembersFlag);
  const setChartData = useChronicBreakdownStore((state) => state.setChartData);
  const setChartLoading = useChronicBreakdownStore((state) => state.setChartLoading);
  const isChartLoading = useChronicBreakdownStore((state) => state.chartLoading);
  const selectedYear = useChronicBreakdownStore((state) => state.selectedYear);
  const setSelectedYear = useChronicBreakdownStore((state) => state.setSelectedYear);
  const selectedChronicCondition = useChronicBreakdownStore((state) => state.selectedChronicCondition);
  const setSelectedChronicCondition = useChronicBreakdownStore((state) => state.setSelectedChronicCondition);
  const yearOptions = useChronicBreakdownStore((state) => state.yearOptions);
  const setYearOptions = useChronicBreakdownStore((state) => state.setYearOptions);
  const chronicConditionOptions = useChronicBreakdownStore((state) => state.chronicConditionOptions);
  const setChronicConditionOptions = useChronicBreakdownStore((state) => state.setChronicConditionOptions);
  const loadingChronicCondition = useChronicBreakdownStore((state) => state.loadingChronicCondition);
  const setLoadingChronicCondition = useChronicBreakdownStore((state) => state.setLoadingChronicCondition);

  const fetchDropdownOptions = async ({
    attributeName,
    params = [],
    mapFn = (item: any) => ({ label: item.value, id: item.value }),
  }: {
    attributeName: string;
    params?: any[];
    mapFn?: (item: any) => any;
  }) => {
    try {
      const observable = memberDetailsService.fetchAttributeDropdownWithSqParams({
        attributeName,
        sqlParamsFromPayload: params,
      });

      return new Promise<any[]>((resolve, reject) => {
        observable.subscribe({
          next: (response: any) => {
            resolve((response?.data?.data?.data || []).map(mapFn));
          },
          error: (err: any) => {
            console.error(`Error fetching ${attributeName}`, err);
            reject(err);
          },
        });
      });
    } catch (error) {
      console.error(`Error fetching ${attributeName}`, error);
      return [];
    }
  };

  useEffect(() => {
    if (comingFromMembers) {
      setFlag(true);
    }
  }, [comingFromMembers]);

  // YEAR
  useEffect(() => {
    getOptions("YearChronicReportFilter");
    setChartLoading(true);
  }, []);

  useEffect(() => {
    if (!comingFromMembers && filterDropdowns?.YearChronicReportFilter) {
      const actualYears = filterDropdowns.YearChronicReportFilter.map((item: any) => ({
        label: item.value,
        id: item.value,
      }));

      const allOption = { id: "All", label: "Select All" };
      const allAsFirst = [allOption, ...actualYears];

      setYearOptions(allAsFirst);
      setSelectedYear("All");
    }
  }, [filterDropdowns]);

  // STATE
  useEffect(() => {
    const effectiveYear = getEffectiveYear(selectedYear, yearOptions);    
    if (selectedYear?.length) {
      (async () => {
        setLoadingState(true);
        const result = await fetchDropdownOptions({
          attributeName: "StateChronicReportFilter",
          params: [effectiveYear],
        });
        setStateOptions(result);
        setSelectedState(flag ? prevAppliedFilters[1] : result.map((i) => i.id));
        setLoadingState(false);
      })();
    } else {
      setStateOptions([]);
      setSelectedState([]);
    }
  }, [selectedYear]);

  // CITY
  useEffect(() => {
    const effectiveYear = getEffectiveYear(selectedYear, yearOptions);
    if (selectedState?.length) {
      (async () => {
        setLoadingCity(true);
        const result = await fetchDropdownOptions({
          attributeName: "CityChronicReportFilter",
          params: [selectedState, effectiveYear],
        });
        setCityOptions(result);
        setSelectedCity(flag ? prevAppliedFilters[2] : result.map((i) => i.id));
        setLoadingCity(false);
      })();
    } else {
      setCityOptions([]);
      setSelectedCity([]);
    }
  }, [selectedState]);

  // HEALTH PLAN
  useEffect(() => {
    const effectiveYear = getEffectiveYear(selectedYear, yearOptions);
    if (selectedCity?.length) {
      (async () => {
        setLoadingHealthPlan(true);
        const result = await fetchDropdownOptions({
          attributeName: "InsuranceChronicReportFilter",
          params: [selectedCity, selectedState, effectiveYear],
        });
        setHealthPlanOptions(result);
        setSelectedHealthPlan(flag ? prevAppliedFilters[3] : result.map((i) => i.id));
        setLoadingHealthPlan(false);
      })();
    } else {
      setHealthPlanOptions([]);
      setSelectedHealthPlan([]);
    }
  }, [selectedCity]);

  // PCP VENDOR
  useEffect(() => {
    const effectiveYear = getEffectiveYear(selectedYear, yearOptions);
    if (selectedHealthPlan?.length) {
      (async () => {
        setLoadingPcpVendor(true);
        const result = await fetchDropdownOptions({
          attributeName: "PCPVendorChronicReportFilter",
          params: [selectedHealthPlan, selectedCity, selectedState, effectiveYear],
        });
        setPcpVendorOptions(result);
        setSelectedPcpVendor(flag ? prevAppliedFilters[4] : result.map((i) => i.id));
        setLoadingPcpVendor(false);
      })();
    } else {
      setPcpVendorOptions([]);
      setSelectedPcpVendor([]);
    }
  }, [selectedHealthPlan]);

  // PROVIDERS
  useEffect(() => {
    const effectiveYear = getEffectiveYear(selectedYear, yearOptions);
    if (selectedPCPVendor?.length) {
      (async () => {
        setLoadingProviders(true);
        const result = await fetchDropdownOptions({
          attributeName: "PCPChronicReportFilter",
          params: [selectedPCPVendor, selectedHealthPlan, selectedCity, selectedState, effectiveYear],
          mapFn: (item: any) => ({ label: item.value, id: item.key }),
        });
        setProviderOptions(result);
        setSelectedProvider(flag ? prevAppliedFilters[5] : result.map((i) => i.id));
        setLoadingProviders(false);
      })();
    } else {
      setProviderOptions([]);
      setSelectedProvider([]);
    }
  }, [selectedPCPVendor]);

  // CHRONIC CONDITIONS
  useEffect(() => {
    const effectiveYear = getEffectiveYear(selectedYear, yearOptions);
    if (selectedProvider?.length) {
      (async () => {
        setLoadingChronicCondition(true);
        const result = await fetchDropdownOptions({
          attributeName: "ChronicNamesChronicReportFilter",
          params: [selectedProvider, selectedPCPVendor, selectedHealthPlan, selectedCity, selectedState, effectiveYear],
        });
        setChronicConditionOptions(result);
        setSelectedChronicCondition(flag ? prevAppliedFilters[6] : result.map((i) => i.id));
        setLoadingChronicCondition(false);
        setFlag(false);
      })();
    } else {
      setChronicConditionOptions([]);
      setSelectedChronicCondition([]);
    }
  }, [selectedProvider]);  

  useEffect(() => {
    const allFiltersAvailable = !isEmpty(selectedState) && !isEmpty(selectedCity) && !isEmpty(selectedHealthPlan) && !isEmpty(selectedPCPVendor) && !isEmpty(selectedProvider) && !isEmpty(selectedChronicCondition);
    const allDropdownsLoaded = !loadingState && !loadingCity && !loadingHealthPlan && !loadingPcpVendor && !loadingProviders && !loadingChronicCondition;

    if (comingFromMembers && allDropdownsLoaded) {
      const effectiveYear = getEffectiveYear(prevAppliedFilters[0], yearOptions);
      if (effectiveYear.length === yearOptions.length - 1) {
        setSelectedYear("All");
      } else {
        setSelectedYear(effectiveYear[0]);
      }

      setSelectedState(prevAppliedFilters[1]);
      setSelectedCity(prevAppliedFilters[2]);
      setSelectedHealthPlan(prevAppliedFilters[3]);
      setSelectedPcpVendor(prevAppliedFilters[4]);
      setSelectedProvider(prevAppliedFilters[5]);
      setSelectedChronicCondition(prevAppliedFilters[6]);

      handleApplyFilter(effectiveYear, prevAppliedFilters[1], prevAppliedFilters[2], prevAppliedFilters[3], prevAppliedFilters[4], prevAppliedFilters[5], prevAppliedFilters[6]);

      setFlag(true);
      setHasInitialFilterRun(true);
      handleComingFromMembersFlag(false);
    } else if (allFiltersAvailable && !hasInitialFilterRun) {
      handleApplyFilter(selectedYear, selectedState, selectedCity, selectedHealthPlan, selectedPCPVendor, selectedProvider, selectedChronicCondition);
      setHasInitialFilterRun(true);
    }
  }, [comingFromMembers, hasInitialFilterRun, selectedYear, selectedState, selectedCity, selectedHealthPlan, selectedPCPVendor, selectedProvider, selectedChronicCondition]);

  const resetAllFilters = () => {
    setSelectedYear([])
    setSelectedState([]);
    setSelectedCity([]);
    setSelectedHealthPlan([]);
    setSelectedPcpVendor([]);
    setSelectedProvider([]);
    setSelectedChronicCondition([])
    setStateOptions([])
    setCityOptions([]);
    setHealthPlanOptions([]);
    setPcpVendorOptions([]);
    setProviderOptions([]);
    setChronicConditionOptions([])
  };

  useEffect(() => {
    return () => {
      resetAllFilters();
    };
  }, []);

  const handleSelectChange = (value: any) => {
    setSelectedChronicCondition(value);
  };

  const { Panel } = Collapse;

  const handleApplyFilter = (selectedYear: any, selectedState: any, selectedCity: any, selectedHealthPlan: any, selectedPCPVendor: any, selectedProvider: any, selectedChronicCondition: any) => {
    const effectiveYear = getEffectiveYear(selectedYear, yearOptions);
    setChartLoading(true);
    setPrevAppliedFilters([effectiveYear, selectedState, selectedCity, selectedHealthPlan, selectedPCPVendor, selectedProvider, selectedChronicCondition]);
    reportService.getChronicBreakdownChartData(effectiveYear, selectedState, selectedCity, selectedHealthPlan, selectedPCPVendor, selectedProvider, selectedChronicCondition).subscribe({
      next: (response: any) => {
        setChartData(response.data.data);
        setChartLoading(false);
      },
      error: (error) => {
        console.error(error);
        setChartLoading(false);
      },
      complete: () => setChartLoading(false),
    });
  };

  return (
    <Spin spinning={loadingState || loadingCity || loadingHealthPlan || loadingOptions || loadingPcpVendor || loadingProviders || loadingChronicCondition} indicator={<HealthemSpinner />}>
      <div className="filter-section__panel mr-05">
        <Collapse className="filter-section__panel__collapse" expandIconPosition="end" defaultActiveKey={["1"]}>
          <Panel header="Year" key="1">
            <Select style={{ width: "100%" }} value={selectedYear} onChange={(value) => setSelectedYear(value)}>
              {yearOptions.map((year: any) => (
                <Select.Option key={year.id} value={year.id}>
                  {year.label}
                </Select.Option>
              ))}
            </Select>
          </Panel>
          <Panel header="State" key="2">
            <MultiSelectOpen titleHeader="State" options={stateOptions} value={selectedState} handleSelect={setSelectedState} isSearchRequired={false} />
          </Panel>
          <Panel header="City" key="3">
            <MultiSelectOpen titleHeader="City" options={cityOptions} value={selectedCity} handleSelect={setSelectedCity} isSearchRequired={false} />
          </Panel>
          <Panel header="Health Plan" key="4">
            <MultiSelectOpen titleHeader="Health Plans" options={healthPlanOptions} value={selectedHealthPlan} handleSelect={setSelectedHealthPlan} isSearchRequired={false} />
          </Panel>
          <Panel header="PCP Vendor" key="5">
            <MultiSelectOpen titleHeader="PCP Vendor" options={pcpVendorOptions} value={selectedPCPVendor} handleSelect={setSelectedPcpVendor} isSearchRequired={false} />
          </Panel>
          <Panel header="PCP" key="6">
            <MultiSelectOpen titleHeader="PCP" options={providerOptions} value={selectedProvider} handleSelect={setSelectedProvider} isSearchRequired={false} />
          </Panel>
          <Panel header="Chronic Conditions" key="7">
            <MultiSelectOpen titleHeader="Chronic Conditions" options={chronicConditionOptions} value={selectedChronicCondition} handleSelect={handleSelectChange} isSearchRequired={false} />
          </Panel>
        </Collapse>
        <div className="filter-section__actions mt-05">
          <Button
            type="primary"
            className="apply-filter"
            style={{ height: "40px" }}
            disabled={isEmpty(selectedChronicCondition) || isChartLoading}
            onClick={() => {
              handleApplyFilter(selectedYear, selectedState, selectedCity, selectedHealthPlan, selectedPCPVendor, selectedProvider, selectedChronicCondition);
            }}
          >
            Apply Filter
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export default Filters;